import { useEffect, useRef, useState } from 'react';

/**
 * Хук определяет, является ли элемент позиционированным со значением "sticky".
 *
 * @param options - Опции для IntersectionObserver.
 * @returns Кортеж [ref, isTop, isSticky].
 */
export const useSticky = <T extends HTMLElement>(
  options: IntersectionObserverInit & {
    topOffset?: number;
    onStickyChange?: (isSticky: boolean) => void;
  } = {},
): { ref: React.RefObject<T | null>; isTop: boolean; isSticky: boolean } => {
  const ref = useRef<T | null>(null);
  const [isSticky, setIsSticky] = useState(true);
  const [isTop, setIsTop] = useState(true);

  const { root, rootMargin = '0%', topOffset = 1, onStickyChange } = options;

  useEffect(() => {
    if (!ref.current || root === undefined) {
      return;
    }

    const observer = new IntersectionObserver(
      ([event]) => setIsTop(event.intersectionRatio < 1),
      { threshold: 1, root, rootMargin: `-${topOffset}px 0px 0px 0px` },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [root, topOffset]);

  useEffect(() => {
    if (!ref.current || root === undefined) {
      return;
    }

    const observer = new IntersectionObserver(
      ([event]) => {
        const newIsSticky = event.intersectionRatio === 1;

        setIsSticky(newIsSticky);
        onStickyChange?.(newIsSticky);
      },
      { threshold: 1, root, rootMargin },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [root, rootMargin, onStickyChange]);

  return { ref, isTop, isSticky };
};
